import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { generateClient } from "aws-amplify/api";
import { ArrowLeft, Edit, Plus, Trash2 } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import {
  getLeague,
  listLeaguePointAdjustments,
  listPlayerConstructs,
  listUsers,
} from "../../graphql/queries";
import {
  createLeaguePointAdjustment,
  deleteLeaguePointAdjustment,
  updateLeaguePointAdjustment,
} from "../../graphql/mutations";

const client = generateClient();

const LeaguePointAdjustments: React.FC = () => {
  const { leagueId } = useParams<{ leagueId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [target, setTarget] = useState("");
  const [points, setPoints] = useState("");
  const [reason, setReason] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const { data: league } = useQuery({
    queryKey: ["league", leagueId],
    queryFn: async () => {
      const response = await client.graphql({
        query: getLeague,
        variables: { id: leagueId! },
      });
      return response.data.getLeague;
    },
  });
  const { data: adjustments = [] } = useQuery({
    queryKey: ["leaguePointAdjustments", leagueId],
    queryFn: async () => {
      const response = await client.graphql({
        query: listLeaguePointAdjustments,
        variables: {
          filter: { leagueId: { eq: leagueId } },
          limit: 1000,
        },
      });
      return response.data.listLeaguePointAdjustments.items;
    },
  });
  const { data: constructs = [] } = useQuery({
    queryKey: ["allPlayerConstructs"],
    queryFn: async () => {
      const response = await client.graphql({
        query: listPlayerConstructs,
        variables: { limit: 1000 },
      });
      return response.data.listPlayerConstructs.items;
    },
  });
  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const response = await client.graphql({
        query: listUsers,
        variables: { limit: 1000 },
      });
      return response.data.listUsers.items;
    },
  });

  const candidates = useMemo(() => {
    const mappedUserIds = new Set(
      constructs.map((construct: any) => construct.userId).filter(Boolean)
    );
    return [
      ...constructs.map((construct: any) => ({
        value: `construct:${construct.id}`,
        name: construct.name,
        playerConstructId: construct.id,
        userId: null,
      })),
      ...users
        .filter((user: any) => !mappedUserIds.has(user.id))
        .map((user: any) => ({
          value: `user:${user.id}`,
          name: user.displayName,
          playerConstructId: null,
          userId: user.id,
        })),
    ].sort((a, b) => a.name.localeCompare(b.name));
  }, [constructs, users]);

  const currentAdmin = (() => {
    try {
      const token = sessionStorage.getItem("id_token");
      return token
        ? JSON.parse(atob(token.split(".")[1])).email || "Administrator"
        : "Administrator";
    } catch {
      return "Administrator";
    }
  })();

  const refresh = () => queryClient.invalidateQueries();

  const addAdjustment = async () => {
    const selected = candidates.find((candidate) => candidate.value === target);
    const numericPoints = Number(points);
    if (
      !selected ||
      !Number.isInteger(numericPoints) ||
      numericPoints === 0 ||
      !reason.trim()
    ) {
      alert("Choose a player, enter a non-zero whole number, and add a reason.");
      return;
    }

    setIsSaving(true);
    try {
      await client.graphql({
        query: createLeaguePointAdjustment,
        variables: {
          input: {
            leagueId: leagueId!,
            userId: selected.userId,
            playerConstructId: selected.playerConstructId,
            playerName: selected.name,
            points: numericPoints,
            reason: reason.trim(),
            createdBy: currentAdmin,
          },
        },
      });
      setTarget("");
      setPoints("");
      setReason("");
      await refresh();
    } finally {
      setIsSaving(false);
    }
  };

  const editAdjustment = async (adjustment: any) => {
    const nextPoints = window.prompt(
      "Adjustment points (positive or negative whole number):",
      String(adjustment.points)
    );
    if (nextPoints === null) return;
    const numericPoints = Number(nextPoints);
    const nextReason = window.prompt("Reason:", adjustment.reason);
    if (
      nextReason === null ||
      !nextReason.trim() ||
      !Number.isInteger(numericPoints) ||
      numericPoints === 0
    ) {
      alert("Points must be a non-zero whole number and a reason is required.");
      return;
    }
    await client.graphql({
      query: updateLeaguePointAdjustment,
      variables: {
        input: {
          id: adjustment.id,
          points: numericPoints,
          reason: nextReason.trim(),
        },
      },
    });
    await refresh();
  };

  const removeAdjustment = async (adjustment: any) => {
    if (
      !window.confirm(
        `Delete the ${adjustment.points > 0 ? "+" : ""}${adjustment.points} point adjustment for ${adjustment.playerName}?`
      )
    ) {
      return;
    }
    await client.graphql({
      query: deleteLeaguePointAdjustment,
      variables: { input: { id: adjustment.id } },
    });
    await refresh();
  };

  const netPoints = adjustments.reduce(
    (sum: number, adjustment: any) => sum + adjustment.points,
    0
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          onClick={() => navigate(`/admin/leagues/${leagueId}`)}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          League
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Point Adjustments</h1>
          <p className="text-muted-foreground">
            {league?.name || "League"} · Net {netPoints >= 0 ? "+" : ""}
            {netPoints} points
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Add an adjustment</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2 md:col-span-2">
            <Label>Player</Label>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a player" />
              </SelectTrigger>
              <SelectContent>
                {candidates.map((candidate) => (
                  <SelectItem key={candidate.value} value={candidate.value}>
                    {candidate.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="adjustment-points">Points</Label>
            <Input
              id="adjustment-points"
              type="number"
              step="1"
              value={points}
              onChange={(event) => setPoints(event.target.value)}
              placeholder="Example: 3 or -2"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="adjustment-reason">Reason</Label>
            <Textarea
              id="adjustment-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Bonus, correction, penalty, etc."
            />
          </div>
          <Button
            onClick={addAdjustment}
            disabled={isSaving}
            className="md:col-span-2"
          >
            <Plus className="mr-2 h-4 w-4" />
            {isSaving ? "Saving…" : "Add Adjustment"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Adjustment history</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {[...adjustments]
            .sort((a: any, b: any) => b.createdAt.localeCompare(a.createdAt))
            .map((adjustment: any) => (
              <div
                key={adjustment.id}
                className="flex flex-wrap items-center gap-3 rounded-xl border border-white/10 p-4"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-semibold">{adjustment.playerName}</p>
                  <p className="text-sm text-muted-foreground">
                    {adjustment.reason} · {adjustment.createdBy} ·{" "}
                    {new Date(adjustment.createdAt).toLocaleString()}
                  </p>
                </div>
                <Badge
                  variant={adjustment.points >= 0 ? "default" : "destructive"}
                >
                  {adjustment.points > 0 ? "+" : ""}
                  {adjustment.points}
                </Badge>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => editAdjustment(adjustment)}
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => removeAdjustment(adjustment)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          {adjustments.length === 0 && (
            <p className="py-8 text-center text-muted-foreground">
              No point adjustments have been recorded.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LeaguePointAdjustments;
